import { useEffect, useState } from "react";
import { Avatar, Box, Button, CircularProgress, Paper, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadImage from '../../../images/icons/upload-image.png';
import Fetch from "../../../services/Fetch";
import { useConstants } from "../../../hooks/UseConstants";
import { useTableStyles } from "../../../hooks/UseTableStyles";
import { usePopups } from "../../../hooks/UsePopups";
import { useWaits } from "../../../hooks/UseWait";
import DeleteDialog from "../../../popup/DeleteDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { usePagination } from "../../../hooks/UsePagination";
import { useSearch } from "../../../hooks/UseSearch";
import ModalOverlay from "../../../components/ModalOverlay";

function UniversitiesTab({ setSnackBar }) {
    const intl = useIntl();
    const theme = useTheme();
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch } = useSearch();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [item, setItem] = useState(null);
    const [deleteId, setDeleteId] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [nameAr, setNameAr] = useState('');
    const [logo, setLogo] = useState('');
    const [logoPreview, setLogoPreview] = useState('');

    const fetchList = async () => {
        const result = await Fetch(host + `/universities?page=${page + 1}&search=${encodeURIComponent(search)}`, 'GET', null);
        if (result.status === 200) {
            setItems(result.data.data || []);
            setTotal(result.data.pagination?.total ?? 0);
            setTotalPages(result.data.pagination?.last_page || 1);
            setCurrentPage(page);
        }
        setGetWait(false);
    };

    const resetForm = () => {
        setNameEn('');
        setNameAr('');
        setLogo('');
        setLogoPreview('');
        setItem(null);
    };

    const save = async (isUpdate) => {
        setSendWait(true);
        const formData = new FormData();
        formData.append('name_en', nameEn);
        formData.append('name_ar', nameAr);
        if (logo) formData.append('logo', logo);
        const url = isUpdate
            ? `${host}/admin/universities/${item.id}/update`
            : `${host}/admin/universities/store`;
        const result = await Fetch(url, 'POST', formData);
        if (result.status === 200) {
            setSnackBar('success', <FormattedMessage id={isUpdate ? 'university_updated_msg' : 'university_added_msg'} />);
            await fetchList();
            resetForm();
            setPopup(isUpdate ? 'universities_update' : 'universities_add', 'none');
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors?.[0] || result.data.message);
        }
        setSendWait(false);
    };

    const remove = async () => {
        const result = await Fetch(host + `/admin/universities/${deleteId}/delete`, 'DELETE', null);
        if (result.status === 200) {
            setItems((list) => list.filter((i) => i.id !== deleteId));
            setSnackBar('success', <FormattedMessage id="university_deleted_msg" />);
            setPopup('universities_delete', 'none');
        }
    };

    useEffect(() => {
        setGetWait(true);
        fetchList();
    }, [page, search]);

    const FormPopup = ({ id, titleId, onSave }) => (
        <ModalOverlay id={id} onClose={() => { resetForm(); setPopup(id, 'none'); }}>
            <Box sx={{ backgroundColor: theme.palette.background.paper }} className="shadow-lg w-2/5 min-w-[320px] rounded-3xl px-4 py-5 relative" dir={language === 'en' ? 'ltr' : 'rtl'}>
                <Typography variant="h6"><FormattedMessage id={titleId} /></Typography>
                <CloseIcon onClick={() => { resetForm(); setPopup(id, 'none'); }} className="cursor-pointer absolute top-5 left-5" sx={{ left: language === 'en' && '90%' }} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_ar" />} className="w-full !mt-5" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_en" />} className="w-full !mt-3" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                <Box className="relative w-full h-28 bg-gray-200 rounded-xl mt-4 flex items-center justify-center overflow-hidden">
                    {logoPreview ? <img src={logoPreview} alt="" className="w-full h-full object-contain" /> : <><img src={UploadImage} alt="" /><Typography variant="body2"><FormattedMessage id="university_logo" /></Typography></>}
                    <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => { const f = e.target.files[0]; setLogo(f); if (f) setLogoPreview(URL.createObjectURL(f)); }} />
                </Box>
                <Box className="flex gap-2 mt-5">
                    <Button variant="contained" onClick={onSave} disabled={sendWait}>{sendWait ? <CircularProgress size={20} color="inherit" /> : <FormattedMessage id="save" />}</Button>
                    <Button variant="contained" className="!bg-gray-400" onClick={() => setPopup(id, 'none')}><FormattedMessage id="cancel" /></Button>
                </Box>
            </Box>
        </ModalOverlay>
    );

    return (
        <>
            {getWait ? <Box className="flex justify-center py-20"><CircularProgress size={50} /></Box> : (
                <Box sx={{ backgroundColor: theme.palette.background.paper }} className="rounded-xl px-2">
                    <Box className="flex justify-between items-center px-2 py-2">
                        <Typography variant="h6"><FormattedMessage id="universities" /></Typography>
                        <Button variant="contained" onClick={() => { resetForm(); setPopup('universities_add', 'flex'); }}><AddIcon /><FormattedMessage id="add_university" /></Button>
                    </Box>
                    <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : 'rtl'}>
                        <Box className="py-2 px-2 flex justify-between">
                            <Box className="w-2/4 relative">
                                <input style={{ backgroundColor: theme.palette.background.default }} onChange={(e) => { setPage(0); setSearch(e.target.value); }} className="w-full h-12 rounded-md border indent-14 outline-none" placeholder={intl.formatMessage({ id: 'search_universities' })} />
                                <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" />
                            </Box>
                            <Typography variant="body2" className="text-gray-500"><FormattedMessage id="total_universities" />: {total}</Typography>
                        </Box>
                        <Table>
                            <TableHead><TableRow>
                                <StyledTableCell><FormattedMessage id="category_name" /></StyledTableCell>
                                <StyledTableCell align="center"><FormattedMessage id="procedures" /></StyledTableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {items.map((row) => (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell>
                                            <Box className="flex items-center gap-2">
                                                <Avatar src={row.logo} variant="rounded" />
                                                {language === 'en' ? row.name_en : row.name_ar}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell align="center" className="!flex justify-center gap-2">
                                            <Button size="small" variant="contained" className="!bg-green-300 !text-green-800" onClick={() => { setItem(row); setNameEn(row.name_en); setNameAr(row.name_ar); setLogoPreview(row.logo || ''); setPopup('universities_update', 'flex'); }}><FormattedMessage id="update" /></Button>
                                            <Button size="small" variant="contained" className="!bg-red-300 !text-red-700" onClick={() => { setDeleteId(row.id); setPopup('universities_delete', 'flex'); }}><FormattedMessage id="delete" /></Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box className="flex justify-center py-3" dir="rtl">
                            <Button disabled={page + 1 >= totalPages} onClick={() => setPage(currentPage + 1)}><NavigateNextIcon /></Button>
                            <Typography dir="ltr">{currentPage + 1} / {totalPages}</Typography>
                            <Button disabled={page === 0} onClick={() => setPage(currentPage - 1)}><NavigateBeforeIcon /></Button>
                        </Box>
                    </TableContainer>
                </Box>
            )}
            <FormPopup id="universities_add" titleId="add_university" onSave={() => save(false)} />
            <FormPopup id="universities_update" titleId="update_university" onSave={() => save(true)} />
            <ModalOverlay id="universities_delete" onClose={() => setPopup('universities_delete', 'none')}>
                <DeleteDialog onClickConfirm={remove} onClickCancel={() => setPopup('universities_delete', 'none')} title={<FormattedMessage id="university_delete" />} subtitle={<FormattedMessage id="university_delete_description" />} />
            </ModalOverlay>
        </>
    );
}

export default UniversitiesTab;
